// Estado e contas do popup de produto (combo/etapas). Verbatim do antigo
// TotemProductPopup em pages/BalcaoVendas.js — só virou hook.
import { useState, useEffect } from "react";
import { toast } from "sonner";

import { sinalDaEtapa, valorDaEtapa } from "../../../utils/precoEtapas";
import { precoPromocional } from "../lib/promocao";
import { useBalanca } from "../../balanca/useBalanca";
import { vendePorPeso } from "./QuantidadePeso";

// Acima disso (kg ou L) o Adicionar pede confirmação — DialogoPesoAlto.
const PESO_ALTO = 30;

// `modulo` (26/10/2026): o Balcão passa "Balcao" e o Totem passa "Totem" para o
// quadro de peso ler a balança do computador (features/balanca). No Totem a
// balança é obrigatória: produto Pesar na balança só entra pesado.
export function usePopupProduto({ product, open, onClose, onAddToCart, allProducts = [], activePromotions = [], modulo = null }) {
  const [pesadoBalanca, setPesadoBalanca] = useState(false);
  const balanca = useBalanca({ modulo, produto: product });
  const pesoObrigatorio = modulo === "Totem" && balanca.ativa;

  const [imageError, setImageError] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [observation, setObservation] = useState("");
  const [comboStep, setComboStep] = useState(0);
  const [selectedComboType, setSelectedComboType] = useState(null);
  const [stepSelections, setStepSelections] = useState({});
  const [itemImageErrors, setItemImageErrors] = useState({});
  const [stepSearch, setStepSearch] = useState("");
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [pesoAConfirmar, setPesoAConfirmar] = useState(null); // peso > 30 esperando o "Confirmar"

  const handleRequestClose = (openState) => {
    if (openState === false) { setShowDiscardConfirm(true); return; }
  };

  const initializeDefaultSelections = (steps, filterComboOnly = false, comboType = null) => {
    const initial = {};
    let relevant = steps;
    if (filterComboOnly && comboType === "simples") {
      relevant = steps.filter((s) => !s.combo_only);
    }
    relevant.forEach((step, i) => {
      if (step.default_item_id) {
        const exists = step.items?.some((it) => it.product_id === step.default_item_id);
        if (exists) initial[i] = [step.default_item_id];
      }
    });
    return initial;
  };

  useEffect(() => {
    if (open && product) {
      setQuantity(1);
      setPesadoBalanca(false);
      setObservation("");
      setImageError(false);
      setSelectedComboType(null);
      setItemImageErrors({});
      setStepSearch("");
      setShowDiscardConfirm(false);
      setPesoAConfirmar(null);
      const isCombo = product.product_type === "combo";
      const hasSteps = product.order_steps?.length > 0;
      if (!isCombo && hasSteps) {
        setStepSelections(initializeDefaultSelections(product.order_steps));
        setComboStep(1);
      } else {
        setStepSelections({});
        setComboStep(0);
      }
    }
  }, [open, product]);

  useEffect(() => { setStepSearch(""); }, [comboStep]);

  const handleSelectComboType = (type) => {
    setSelectedComboType(type);
    if (product?.order_steps?.length > 0) {
      setStepSelections(initializeDefaultSelections(product.order_steps, true, type));
    }
  };

  const p = product || {};

  const isCombo = p.product_type === "combo";
  const hasOrderSteps = p.order_steps?.length > 0;
  const comboPrice = p.sale_price || 0;
  const simplePrice = p.simple_price || Math.round(comboPrice * 0.7 * 100) / 100;

  // Promos
  const getPopupPromo = (ct) => {
    for (const promo of activePromotions) {
      // O item da promoção casa com o produto DO POPUP (antes comparava o
      // próprio item consigo mesmo e a promoção nunca entrava no preço).
      const pp = (promo.products || []).find((item) => item.product_id === p.id);
      if (pp) {
        if (ct === "combo" && pp.discount_value_combo) return { promo, value: pp.discount_value_combo, type: promo.discount_type };
        // Pizza por tamanho — sem valor único pra exibir; popup pizza mostra preço promo por tamanho.
        if (pp.sizes && typeof pp.sizes === 'object' && pp.discount_value == null) return null;
        return { promo, value: pp.discount_value, type: promo.discount_type };
      }
    }
    return null;
  };
  const calcPromo = precoPromocional;
  const promoSimple = getPopupPromo("simples");
  const promoCombo = getPopupPromo("combo");
  const simplePricePromo = isCombo ? calcPromo(simplePrice, promoSimple) : calcPromo(comboPrice, promoSimple);
  const comboPricePromo = calcPromo(comboPrice, promoCombo);

  const getRelevantSteps = () => {
    if (!hasOrderSteps) return [];
    return p.order_steps.filter((s) => (selectedComboType === "simples" ? !s.combo_only : true));
  };
  const relevantSteps = getRelevantSteps();
  const totalSteps = relevantSteps.length;
  const totalStepsWithSummary = totalSteps + 1;
  const isOnSummaryStep = comboStep > totalSteps && comboStep > 0;

  const getProductPhoto = (pid) => allProducts.find((p) => p.id === pid)?.photo_url || null;
  const isProductAvailable = (pid) => allProducts.find((p) => p.id === pid)?.available !== false;
  // Produto inexistente em allProducts = foi arquivado/deletado (não vem mais em /products) → trata como hidden.
  const isProductHidden = (pid) => {
    const p = allProducts.find((p) => p.id === pid);
    return !p || p.hidden === true;
  };
  const getProductPrice = (pid) => allProducts.find((p) => p.id === pid)?.sale_price || 0;
  const getProductDescription = (pid) => allProducts.find((p) => p.id === pid)?.description || "";
  const getItemPrice = (item, step) => {
    if (step?.sync_prices !== false) return getProductPrice(item.product_id);
    if (item.price_override === 0) return 0;
    if (item.price_override > 0) return item.price_override;
    return getProductPrice(item.product_id);
  };

  const calculateTotalPrice = () => {
    let base;
    if (!isCombo) base = simplePricePromo != null ? simplePricePromo : comboPrice;
    else if (selectedComboType === "combo") base = comboPricePromo != null ? comboPricePromo : comboPrice;
    else base = simplePricePromo != null ? simplePricePromo : simplePrice;

    if (hasOrderSteps) {
      relevantSteps.forEach((step, i) => {
        const sel = stepSelections[i] || [];
        // Soma, Máximo ou Subtração: regra única em utils/precoEtapas.js
        const precos = sel.map((id) => step.items?.find((x) => x.product_id === id)).filter(Boolean).map((it) => getItemPrice(it, step));
        base += valorDaEtapa(step, precos);
      });
    }
    return Math.max(0, base) * quantity;
  };
  const totalPrice = calculateTotalPrice();

  const canAdvanceStep = () => {
    if (comboStep === 0) return selectedComboType !== null;
    if (isOnSummaryStep) return true;
    const idx = comboStep - 1;
    const step = relevantSteps[idx];
    if (!step) return true;
    return (stepSelections[idx] || []).length >= (step.min_selections || 0);
  };

  const handleNextStep = () => {
    if (comboStep === 0) {
      if (!hasOrderSteps || relevantSteps.length === 0) setComboStep(1);
      else setComboStep(1);
    } else if (comboStep <= totalSteps) setComboStep(comboStep + 1);
    else handleAdd();
  };

  const toggleItemSelection = (stepIdx, itemId) => {
    const step = relevantSteps[stepIdx];
    if (!step) return;
    const cur = stepSelections[stepIdx] || [];
    const isSel = cur.includes(itemId);
    const max = step.max_selections || 999;
    if (isSel) {
      setStepSelections({ ...stepSelections, [stepIdx]: cur.filter((x) => x !== itemId) });
    } else if (max === 1) {
      setStepSelections({ ...stepSelections, [stepIdx]: [itemId] });
    } else if (cur.length < max) {
      setStepSelections({ ...stepSelections, [stepIdx]: [...cur, itemId] });
    }
  };

  const getSelectionsSummary = () => {
    const summary = [];
    relevantSteps.forEach((step, i) => {
      const sel = stepSelections[i] || [];
      const items = sel.map((id) => {
        const it = step.items?.find((x) => x.product_id === id);
        return { name: it?.product_name || "", price: it ? getItemPrice(it, step) : 0 };
      }).filter((x) => x.name);
      if (items.length > 0) summary.push({ stepName: step.name, items, stepIndex: i, sinal: sinalDaEtapa(step) });
    });
    return summary;
  };

  const adicionar = (qtd) => {
    onAddToCart({ ...product, quantity: qtd, observation: observation.trim() || null, combo_type: isCombo ? selectedComboType : null, step_selections: stepSelections, final_price: totalPrice / quantity, ...(pesadoBalanca ? { pesado_balanca: true } : {}) });
    onClose();
  };
  // `qtdDigitada`: o peso que acabou de ser digitado (Enter no campo do peso,
  // antes do estado atualizar). Clique no botão passa o evento → vale `quantity`.
  // Peso acima de 30 kg/L pede confirmação antes (30/09/2026).
  const handleAdd = (qtdDigitada) => {
    // Autoatendimento: sem pesar, não entra (o cliente não digita o peso)
    if (pesoObrigatorio && !pesadoBalanca) { toast.warning("Coloque o produto na balança para pesar"); return; }
    const qtd = typeof qtdDigitada === "number" && qtdDigitada > 0 ? qtdDigitada : quantity;
    if (vendePorPeso(product) && qtd > PESO_ALTO) { setPesoAConfirmar(qtd); return; }
    adicionar(qtd);
  };
  const dialogoPeso = {
    peso: pesoAConfirmar, unidade: product?.sale_unit,
    onConfirmar: () => { const q = pesoAConfirmar; setPesoAConfirmar(null); if (q) adicionar(q); },
    onCorrigir: () => setPesoAConfirmar(null),
  };
  return { modulo, setPesadoBalanca, pesadoBalanca, balancaAtiva: balanca.ativa, pesoObrigatorio, imageError, setImageError, quantity, setQuantity, observation, setObservation, comboStep, setComboStep, selectedComboType, setSelectedComboType, stepSelections, setStepSelections, itemImageErrors, setItemImageErrors, stepSearch, setStepSearch, showDiscardConfirm, setShowDiscardConfirm, handleRequestClose, initializeDefaultSelections, handleSelectComboType, isCombo, hasOrderSteps, comboPrice, simplePrice, getPopupPromo, calcPromo, promoSimple, promoCombo, simplePricePromo, comboPricePromo, getRelevantSteps, relevantSteps, totalSteps, totalStepsWithSummary, isOnSummaryStep, getProductPhoto, isProductAvailable, isProductHidden, getProductPrice, getProductDescription, getItemPrice, calculateTotalPrice, totalPrice, canAdvanceStep, handleNextStep, toggleItemSelection, getSelectionsSummary, handleAdd, dialogoPeso, product, open, onClose, onAddToCart, allProducts, activePromotions };
}
